import Tooltip from '@mui/material/Tooltip';
import React, { useEffect } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { pdfApi, wordApi } from '../../../api';
import downloadSvg from '../../../assets/img/download.svg';
import folderSvg from '../../../assets/img/folder.svg';
import trashSvg from '../../../assets/img/trash.svg';
import { getUserType } from '../../../redux/slices/userSlice';
import { useModal } from '../../../shared/hooks';
import { ItemActionModal } from '../../../shared/ui/ItemActionModal';
import { convertBytes } from '../../../utils/bytesConvertor';
import getFileSize from '../../../utils/getFileSize';

interface Props {
  url: string;
  currentICLId: string;
  type: 'pdf' | 'doc';
  id: string;
  student: boolean;
  downloadFile: (url: string) => void;
}

export default function StudentDocumentItem({ url, currentICLId, type, id, student, downloadFile }: Props) {
  const { t } = useTranslation();
  const { isOpen: isModalOpen, close: setIsModalClose, open: setIsModalOpen } = useModal();
  const [fileSize, setFileSize] = React.useState<string | null>(null);
  const userTypeSelector = useSelector(getUserType);

  useEffect(() => {
    getFileSize(url).then(size => setFileSize(size));
  }, [url]);

  const removeFile = async () => {
    if (!url) {
      return;
    }

    try {
      switch (type) {
        case 'pdf':
          await pdfApi.removePdf(url, currentICLId, id, true);
          break;
        case 'doc':
          await wordApi.removeWord(url, currentICLId, id, true);
          break;
        default:
          break;
      }

      toast.success(t('Successfully removed!'));
    } catch (error) {
      console.log(error);
      toast.error(t('Something went wrong. Try again!'));
    }
  };

  const renderSize = () => {
    return (
      fileSize && (
        <div className="flex flex-col">
          <div className="text-[12px] lg:text-[16px]">
            {type === 'pdf' ? `${t('document')} PDF` : `${t('document')} Word`}
          </div>
          <div className="text-[12px] text-[#656D7B] lg:text-[16px]">
            {convertBytes(+fileSize).megabytes < 0
              ? `${Math.floor(convertBytes(+fileSize).megabytes)} ${t('mb')}`
              : `${Math.floor(convertBytes(+fileSize).kilobytes)} ${t('kb')}`}
          </div>
        </div>
      )
    );
  };

  return (
    <div className="flex items-center justify-between rounded-[10px] border-[1px] border-[#9CDFFC] bg-white p-[20px]">
      <div className="flex items-center">
        <div className="mr-[10px] flex items-center justify-center rounded-[10px] bg-[#DCF0FF] p-[15px] lg:mr-[25px]">
          <img src={folderSvg} alt="folder" />
        </div>

        {renderSize()}
      </div>

      <div className="flex items-center gap-[15px]">
        <Tooltip
          title={t('download file')}
          componentsProps={{
            popper: {
              sx: {
                ['& .MuiTooltip-arrow']: {
                  color: '#9CDFFC',
                },
                ['& .MuiTooltip-tooltip']: {
                  backgroundColor: '#9CDFFC',
                  color: '#000000',
                  fontSize: '14px',
                  padding: '14px',
                  borderRadius: '8px',
                },
              },
            },
          }}
          arrow
        >
          <button
            className="btn more small h-[40px] w-[40px] rounded-[8px] !p-[6px] text-red-600"
            onClick={() => downloadFile(url)}
          >
            <img src={downloadSvg} alt="downloadSvg" />
          </button>
        </Tooltip>

        {student && userTypeSelector === 'Student' && (
          <Tooltip
            title={t('deleteFile')}
            componentsProps={{
              popper: {
                sx: {
                  ['& .MuiTooltip-arrow']: {
                    color: '#9CDFFC',
                  },
                  ['& .MuiTooltip-tooltip']: {
                    backgroundColor: '#9CDFFC',
                    color: '#000000',
                    fontSize: '14px',
                    padding: '14px',
                    borderRadius: '8px',
                  },
                },
              },
            }}
            arrow
          >
            <button
              className="btn more small h-[40px] w-[40px] rounded-[8px] !p-[6px] text-red-600"
              onClick={() => setIsModalOpen()}
            >
              <img src={trashSvg} alt="mic" />
            </button>
          </Tooltip>
        )}
      </div>

      <ItemActionModal
        isOpen={isModalOpen}
        close={setIsModalClose}
        title={'deleteFile'}
        onHandle={() => removeFile()}
        buttonTitle={'delete'}
        isSubmitButtonDisabled={false}
      />
    </div>
  );
}
